/**
 * Guest-list parties, plus-one counts, and venue-capacity allocation.
 *
 * Order in the list is priority (top = highest). A venue sets how many spots
 * are available for the night. Walk top to bottom:
 *   - whole party fits → in (green)
 *   - no spots left → waitlist (yellow/orange)
 *   - some of the party would fit but not all → conflict (red)
 * Conflicts cannot be auto-notified as "you're in" or "you're waitlisted".
 */

const PLUS_RE = /\+(\d+)\b/;
const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const COUNT_ONLY_RE = /^\+\s*(\d+)\s*$/;

const NOTE_HINTS = [
  'promo',
  'credential',
  'comps',
  'comp',
  'photo pass',
  'laminate',
  'will-call',
  'will call',
  'first three',
  'security',
  'accessible',
  'aaa',
];

export const GUEST_CAP_TAB = 'GUEST_CAP';
export const SPOTS_PLACEHOLDER = '0';
export const SPOTS_INPUT_MAX = 4;

export const GUEST_ALLOC = {
  IN: 'in',
  WAITLIST: 'waitlist',
  CONFLICT: 'conflict',
  CUT: 'cut',
  UNSET: 'unset',
};

/** Digits only so the spots field cannot take letters or symbols. */
export function sanitizeSpotCountInput(raw) {
  return String(raw ?? '').replace(/[^\d]/g, '').slice(0, SPOTS_INPUT_MAX);
}

export function parseGuestCap(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  const s = String(raw).trim();
  try {
    const o = JSON.parse(s);
    if (o && typeof o === 'object' && o.spots != null && o.spots !== '') {
      const n = Number(o.spots);
      return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
    }
  } catch {
    /* fall through */
  }
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
}

export function serializeGuestCap(spots) {
  if (spots == null || spots === '') return JSON.stringify({ v: 1, spots: null });
  const n = Number(spots);
  const value = Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
  return JSON.stringify({ v: 1, spots: value });
}

function lineText(line) {
  return (line?.fields || [])
    .map((f) => String(f || '').trim())
    .filter(Boolean)
    .join(' — ');
}

function looksLikeNote(text) {
  const t = String(text || '').trim();
  if (!t) return false;
  if (EMAIL_RE.test(t)) return true;
  const lower = t.toLowerCase();
  return NOTE_HINTS.some((h) => lower.includes(h));
}

function looksLikeName(text) {
  const t = String(text || '').trim();
  if (!t || COUNT_ONLY_RE.test(t) || EMAIL_RE.test(t) || looksLikeNote(t)) return false;
  if (PLUS_RE.test(t) && /tour party|plus[\s-]?one|\+\d+/i.test(t) && t.length < 28) return false;
  return true;
}

export function partySizeFromText(title, lines = []) {
  const titleText = String(title || '').trim();
  const texts = [titleText, ...lines.map((l) => lineText(l) || String(l || '').trim())].filter(Boolean);

  for (const t of texts) {
    const only = t.match(COUNT_ONLY_RE);
    if (only) return Math.max(0, parseInt(only[1], 10));
  }

  for (const t of texts) {
    const plus = t.match(PLUS_RE);
    if (plus) return Math.max(1, 1 + parseInt(plus[1], 10));
  }

  const named = texts.slice(1).filter(looksLikeName);
  if (named.length) return named.length;
  return titleText ? 1 : 0;
}

export function guestNamesFromParty(title, lines = [], size) {
  const titleText = String(title || '').trim();
  const texts = lines.map((l) => lineText(l) || String(l || '').trim()).filter(Boolean);
  const names = texts.filter(looksLikeName);
  const n = Math.max(0, Number(size) || 0);
  const out = names.slice();
  if (!out.length && titleText && n > 0 && !COUNT_ONLY_RE.test(titleText) && !PLUS_RE.test(titleText)) {
    out.push(titleText);
  }
  let i = out.length + 1;
  while (out.length < n) {
    out.push(out.length === 0 ? 'Guest 1' : `Plus-one ${i - 1}`);
    i += 1;
  }
  return out.slice(0, n || out.length);
}

export function guestNoteFromParty(title, lines = []) {
  const texts = [String(title || '').trim(), ...lines.map((l) => lineText(l) || String(l || '').trim())]
    .filter(Boolean);
  const notes = texts.filter((t, idx) => {
    if (idx === 0) return false;
    if (COUNT_ONLY_RE.test(t)) return false;
    if (EMAIL_RE.test(t)) return t;
    if (looksLikeNote(t)) return true;
    if (PLUS_RE.test(t) && !looksLikeName(t)) return true;
    return false;
  });
  return notes.filter((t) => !EMAIL_RE.test(t) && !/^cut(\s+stop)?$/i.test(t)).join(' · ');
}

function partyIsCut(lines) {
  return (Array.isArray(lines) ? lines : []).some((ln) => {
    const t = (Array.isArray(ln?.fields) ? ln.fields : [ln]).join(' ').trim();
    return /^cut(\s+stop)?$/i.test(t);
  });
}

export function parseGuestParties(songs = []) {
  return (songs || [])
    .map((song, index) => {
      const title = String(song?.title || '').trim();
      const lines = Array.isArray(song?.lines) ? song.lines : [];
      const size = partySizeFromText(title, lines);
      if (!title && size <= 0) return null;
      return {
        id: String(song?.id || `g-${index}`),
        who: title || 'Guest party',
        size,
        note: guestNoteFromParty(title, lines),
        names: guestNamesFromParty(title, lines, size),
        cut: partyIsCut(lines),
        song,
      };
    })
    .filter(Boolean);
}

/**
 * @param {{ size: number }[]} parties  already in priority order
 * @param {number|null} spots
 * @returns {{ status: string, remainingAfter: number, spotsUsed: number }[]}
 */
export function allocateGuestParties(parties, spots) {
  const list = Array.isArray(parties) ? parties : [];
  if (spots == null || !Number.isFinite(Number(spots))) {
    return list.map(() => ({ status: GUEST_ALLOC.UNSET, remainingAfter: null, spotsUsed: 0 }));
  }
  let remaining = Math.max(0, Math.floor(Number(spots)));
  return list.map((p) => {
    const size = Math.max(0, Number(p?.size) || 0);
    if (p?.cut) {
      return { status: GUEST_ALLOC.CUT, remainingAfter: remaining, spotsUsed: 0 };
    }
    if (size <= 0) {
      return { status: GUEST_ALLOC.UNSET, remainingAfter: remaining, spotsUsed: 0 };
    }
    if (remaining >= size) {
      remaining -= size;
      return { status: GUEST_ALLOC.IN, remainingAfter: remaining, spotsUsed: size };
    }
    if (remaining <= 0) {
      return { status: GUEST_ALLOC.WAITLIST, remainingAfter: 0, spotsUsed: 0 };
    }
    const used = remaining;
    remaining = 0;
    return { status: GUEST_ALLOC.CONFLICT, remainingAfter: 0, spotsUsed: used };
  });
}

export function guestAllocLabel(status) {
  if (status === GUEST_ALLOC.IN) return 'In';
  if (status === GUEST_ALLOC.WAITLIST) return 'Waitlist';
  if (status === GUEST_ALLOC.CONFLICT) return 'Conflict';
  if (status === GUEST_ALLOC.CUT) return 'Cut';
  return '—';
}

export function toggleGuestPartyCut(song) {
  const lines = Array.isArray(song?.lines) ? song.lines.slice() : [];
  const idx = lines.findIndex((ln) => /^cut(\s+stop)?$/i.test((ln.fields || []).join(' ').trim()));
  if (idx >= 0) lines.splice(idx, 1);
  else lines.push({ id: `gl-cut-${Date.now()}`, fields: ['CUT'] });
  return { ...song, lines };
}

export function patchGuestPartySong(song, { who, note } = {}) {
  let next = { ...song, lines: Array.isArray(song?.lines) ? song.lines.slice() : [] };
  if (who != null) next.title = String(who);
  if (note != null) {
    const text = String(note || '').trim();
    const lines = next.lines.filter((ln) => !/^cut(\s+stop)?$/i.test((ln.fields || []).join(' ').trim()));
    const cutLines = next.lines.filter((ln) => /^cut(\s+stop)?$/i.test((ln.fields || []).join(' ').trim()));
    const nameSet = new Set((guestNamesFromParty(next.title, song?.lines || [], 0) || []).map((n) => n.toLowerCase()));
    const kept = lines.filter((ln) => {
      const t = (ln.fields || []).join(' ').trim();
      if (!t) return false;
      if (nameSet.has(t.toLowerCase())) return true;
      if (EMAIL_RE.test(t)) return true;
      return false;
    });
    if (text) kept.push({ id: `gl-note-${Date.now()}`, fields: [text] });
    next.lines = [...kept, ...cutLines];
  }
  return next;
}

export function countGuestNames(parties) {
  return (parties || []).reduce((n, p) => (p?.cut ? n : n + (Number(p.size) || 0)), 0);
}

export function countByAlloc(results, status) {
  return (results || []).filter((r) => r.status === status).length;
}

export function newGuestPartySong({ who, names = [], note = '', id }) {
  const cleanNames = (names || []).map((n) => String(n || '').trim()).filter(Boolean);
  const title = String(who || '').trim() || (cleanNames[0] || 'Guest party');
  const lines = [];
  const push = (text) => {
    const t = String(text || '').trim();
    if (!t) return;
    lines.push({ id: `gl-${Date.now().toString(36)}-${lines.length}`, fields: [t] });
  };
  if (cleanNames.length) {
    if (cleanNames[0].toLowerCase() !== title.toLowerCase()) {
      cleanNames.forEach((n) => push(n));
    } else {
      cleanNames.slice(1).forEach((n) => push(n));
    }
    if (cleanNames.length > 1) push(`+${Math.max(0, cleanNames.length - (cleanNames[0].toLowerCase() === title.toLowerCase() ? 1 : 0))}`);
  }
  push(note);
  return {
    id: String(id || `g_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`),
    title,
    open: true,
    lines,
  };
}
